<script>
    import { onMount } from 'svelte';
    import Layout from '../components/Layout.svelte';

    let jtPOs = [];
    let xeroPOs = [];
    let posToSync = [];
    let isLoading = false;
    let error = null;
    let selectedPOs = [];
    let selectAll = true;
    let nextPage = null;
    let isLoadingMore = false;

    // Add new filter variables
    let customers = [];
    let selectedFilter = null;
    let selectedJob = null;
    let showContent = false;

    onMount(async () => {
        console.log('Component mounted, fetching jobs...');
        await fetchJobs();
    });

    async function fetchJobs() {
        try {
            const response = await fetch('/api/jt/jobs');
            customers = await response.json();
        } catch (error) {
            console.error('Error fetching jobs:', error);
        }
    }

    async function handleFilterChange(event) {
        selectedFilter = event.target.value;
        if (selectedFilter !== 'job') {
            selectedJob = null;
        }
        if (selectedFilter) {
            showContent = true;
            await fetchAllPOs();
        }
    }

    async function handleJobChange(event) {
        selectedJob = event.target.value;
        if (selectedJob) {
            showContent = true;
            await fetchAllPOs();
        }
    }

    async function fetchMorePOs() {
        if (!nextPage || isLoadingMore) return;
        
        isLoadingMore = true;
        try {
            // Construct URL with filter parameters and page
            let url = '/api/jt/vendor-orders-all';
            const params = new URLSearchParams();

            if (selectedFilter === 'job' && selectedJob) {
                params.append('jobId', selectedJob);
            } else if (selectedFilter !== 'job') {
                params.append('days', selectedFilter);
            }
            
            // Add the nextPage token
            params.append('page', nextPage);

            url = `${url}?${params.toString()}`;
            console.log('🔄 [LOAD MORE] Making request with pagination token:', nextPage);
            
            const jtResponse = await fetch(url);
            if (!jtResponse.ok) {
                throw new Error('Failed to fetch additional JobTread POs');
            }
            
            const jtData = await jtResponse.json();
            console.log('📦 [LOAD MORE] Server returned', jtData.orders.length, 'POs');
            
            if (!Array.isArray(jtData.orders)) {
                throw new Error('Invalid response format from JobTread API');
            }

            // Update the nextPage token first
            const previousToken = nextPage;
            nextPage = jtData.nextPage;
            console.log('🎯 [LOAD MORE] Token update:', {
                previous: previousToken,
                new: nextPage,
                changed: previousToken !== nextPage
            });

            // If we got new orders, add them to the list
            if (jtData.orders && jtData.orders.length > 0) {
                // Create a Set of existing PO numbers for efficient lookup
                // Using PO numbers instead of IDs as they should be unique
                const existingPONumbers = new Set(jtPOs.map(po => po.name));
                
                // Filter out any POs that we already have and log details about duplicates
                const newPOs = jtData.orders.filter(po => {
                    const isDuplicate = existingPONumbers.has(po.name);
                    if (isDuplicate) {
                        console.log('🔄 [LOAD MORE] Duplicate PO found:', {
                            id: po.id,
                            poNumber: po.name,
                            vendor: po.vendor
                        });
                    }
                    return !isDuplicate;
                });

                console.log('✨ [LOAD MORE] Found', newPOs.length, 'unique new POs out of', jtData.orders.length, 'total');
                
                // Add only new POs to the existing list
                if (newPOs.length > 0) {
                    jtPOs = [...jtPOs, ...newPOs];
                    console.log('📈 [LOAD MORE] Updated PO list, new total:', jtPOs.length);
                    // Recompute POs to sync with updated list
                    computePOsToSync();
                } else {
                    console.log('⚠️ [LOAD MORE] No new unique POs found in this batch');
                }
            }
            
            // If we didn't get a new nextPage token or no results were returned,
            // set nextPage to null to indicate we're done
            if (!jtData.nextPage || jtData.orders.length === 0) {
                console.log('🏁 [LOAD MORE] No more pages available, disabling load more');
                nextPage = null;
            }
            
        } catch (err) {
            error = err.message;
            console.error('❌ [LOAD MORE] Error:', err);
        } finally {
            isLoadingMore = false;
        }
    }

    async function fetchAllPOs() {
        if (!selectedFilter) return; // Don't fetch if no filter is selected

        isLoading = true;
        error = null;
        jtPOs = [];
        xeroPOs = [];
        posToSync = [];
        nextPage = null;

        try {
            // Construct URL with filter parameters
            let url = '/api/jt/vendor-orders-all';
            const params = new URLSearchParams();

            if (selectedFilter === 'job' && selectedJob) {
                params.append('jobId', selectedJob);
            } else if (selectedFilter !== 'job') {
                // selectedFilter contains the number of days
                params.append('days', selectedFilter);
            }

            url = `${url}?${params.toString()}`;
            console.log('Fetching JobTread POs from:', url);
            
            let [jtResponse, xeroResponse] = await Promise.all([
                fetch(url),
                fetch('/api/xero/purchase-orders')
            ]);

            // Check if JobTread request failed with 401 and try to refresh session
            if (jtResponse.status === 401) {
                console.log('🔄 [REFRESH] JobTread request failed with 401, attempting to refresh session...');
                
                const refreshResponse = await fetch('/api/refresh-jobtread-session', {
                    method: 'POST',
                    credentials: 'include'
                });
                
                if (refreshResponse.ok) {
                    console.log('✅ [REFRESH] Session refreshed successfully, retrying request...');
                    // Retry the original request
                    jtResponse = await fetch(url);
                    if (!jtResponse.ok) {
                        throw new Error('Failed to fetch JobTread POs after session refresh');
                    }
                } else {
                    throw new Error('Failed to refresh JobTread session. Please log out and log back in.');
                }
            } else if (!jtResponse.ok) {
                throw new Error('Failed to fetch JobTread POs');
            }

            if (!xeroResponse.ok) throw new Error('Failed to fetch Xero POs');

            const jtData = await jtResponse.json();
            console.log('Initial JT response:', jtData);
            console.log('Initial nextPage token:', jtData.nextPage);

            if (!Array.isArray(jtData.orders)) {
                throw new Error('Invalid response format from JobTread API');
            }

            jtPOs = jtData.orders;
            nextPage = jtData.nextPage;
            const xeroData = await xeroResponse.json();
            xeroPOs = xeroData;

            console.log('Initial POs loaded:', jtPOs.length);
            console.log('Next page available:', nextPage ? 'Yes' : 'No');

            computePOsToSync();
        } catch (err) {
            error = err.message;
            console.error('Error fetching POs:', err);
        } finally {
            isLoading = false;
        }
    }

    function computePOsToSync() {
        console.log('Computing POs to sync...');
        console.log('JobTread POs:', jtPOs);
        console.log('Xero POs:', xeroPOs);

        // Filter out deleted Xero POs first
        const activeXeroPOs = xeroPOs.filter(po => po.status !== 'DELETED');
        
        // Create a map of active Xero PO numbers for faster lookup
        const xeroPOMap = new Map(
            activeXeroPOs.map(po => [po.purchaseOrderNumber, po])
        );

        // Filter JobTread POs that need syncing
        posToSync = jtPOs.map(jtPO => {
            const poNumber = jtPO.name.replace('Purchase Order ', '');
            const xeroPO = xeroPOMap.get(poNumber);
            return {
                ...jtPO,
                needsSync: !xeroPO
            };
        });

        console.log('POs to sync:', posToSync);

        // Update selected POs if selectAll is true
        if (selectAll) {
            selectedPOs = posToSync.filter(po => po.needsSync).map(po => po.id);
        }
    }

    function handleSelectAll(event) {
        selectAll = event.target.checked;
        selectedPOs = selectAll ? posToSync.filter(po => po.needsSync).map(po => po.id) : [];
    }

    function handlePOSelect(po, event) {
        if (!po.needsSync) {
            return; // Just return if PO exists in Xero
        }

        if (event.target.checked) {
            selectedPOs = [...selectedPOs, po.id];
        } else {
            selectedPOs = selectedPOs.filter(id => id !== po.id);
            selectAll = false;
        }
    }

    function handleSyncClick() {
        const selectedPOsData = posToSync
            .filter(po => selectedPOs.includes(po.id))
            .map(po => ({
                id: po.id,
                name: po.name,
                vendor: po.vendor,
                createdAt: po.createdAt,
                dueDate: po.dueDate,
                jobId: po.jobId
            }));
        window.location.href = '/sync-progress?pos=' + encodeURIComponent(JSON.stringify(selectedPOsData));
    }

    $: {
        if (jtPOs && xeroPOs) {
            computePOsToSync();
        }
    }

    function calculateTotal(orders) {
        if (!Array.isArray(orders)) return 0;
        return orders.length;
    }
</script>

<Layout>
    <div class="container mx-auto px-4 py-8">
        <h1 class="text-2xl font-bold mb-6">Sync Purchase Orders</h1>

        <!-- Filter Section -->
        <div class="mb-6 p-4 bg-white rounded-lg shadow">
            <h2 class="text-lg font-semibold mb-4">Filter Purchase Orders</h2>
            <div class="space-y-4">
                <p class="text-gray-600 mb-3">Please select a time period or specific job to view purchase orders.</p>
                <div class="flex gap-4">
                    <select
                        class="flex-1 p-2 border rounded bg-white"
                        bind:value={selectedFilter}
                        on:change={handleFilterChange}
                    >
                        <option value="">Please select a filter...</option>
                        <option value="30">Last 30 days</option>
                        <option value="60">Last 60 days</option>
                        <option value="90">Last 90 days</option>
                        <option value="job">By Job</option>
                    </select>

                    {#if selectedFilter === 'job'}
                        <select
                            class="flex-1 p-2 border rounded bg-white"
                            bind:value={selectedJob}
                            on:change={handleJobChange}
                        >
                            <option value="">Select a job...</option>
                            {#each customers as customer}
                                <option value={customer.id}>{customer.name}</option>
                            {/each}
                        </select>
                    {/if}
                </div>
            </div>
        </div>

        {#if showContent}
            {#if isLoading}
                <div class="flex justify-center items-center py-8">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
            {:else if error}
                <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
                    {error}
                </div>
            {:else}
                <!-- Existing content -->
                <div class="bg-white rounded-lg shadow overflow-hidden">
                    <div class="p-4 border-b">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center">
                                <input
                                    type="checkbox"
                                    class="form-checkbox h-5 w-5 text-primary"
                                    bind:checked={selectAll}
                                    on:change={handleSelectAll}
                                >
                                <span class="ml-2 text-gray-700">Select All</span>
                            </div>
                            <button
                                class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
                                disabled={selectedPOs.length === 0}
                                on:click={handleSyncClick}
                            >
                                Sync Selected ({selectedPOs.length})
                            </button>
                        </div>
                    </div>

                    <div class="overflow-x-auto">
                        <table class="min-w-full table-fixed max-w-full">
                            <thead class="bg-white">
                                <tr>
                                    <th class="w-[6%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Select</th>
                                    <th class="w-[20%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">PO Number</th>
                                    <th class="w-[40%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Vendor</th>
                                    <th class="w-[20%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created Date</th>
                                    <th class="w-[14%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-[#edf0f7]">
                                {#each posToSync as po}
                                    <tr class="hover:bg-[#f0f4ff]">
                                        <td class="px-2 py-3 whitespace-nowrap text-sm">
                                            <input
                                                type="checkbox"
                                                class="form-checkbox h-4 w-4 text-primary {po.needsSync ? '' : 'opacity-50 cursor-not-allowed'}"
                                                checked={selectedPOs.includes(po.id)}
                                                disabled={!po.needsSync}
                                                on:change={(event) => handlePOSelect(po, event)}
                                            >
                                        </td>
                                        <td class="px-2 py-3 whitespace-nowrap text-sm truncate max-w-0 overflow-hidden">{po.name}</td>
                                        <td class="px-2 py-3 whitespace-nowrap text-sm truncate max-w-0 overflow-hidden">{po.vendor}</td>
                                        <td class="px-2 py-3 whitespace-nowrap text-sm">{new Date(po.createdAt).toLocaleDateString()}</td>
                                        <td class="px-2 py-3 whitespace-nowrap text-sm">
                                            {#if po.needsSync}
                                                <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                    Needs Sync
                                                </span>
                                            {:else}
                                                <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                    Synced
                                                </span>
                                            {/if}
                                        </td>
                                    </tr>
                                {/each}
                            </tbody>
                        </table>
                    </div>

                    {#if nextPage}
                        <div class="p-4 border-t">
                            <button
                                class="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                on:click={fetchMorePOs}
                                disabled={isLoadingMore}
                            >
                                {isLoadingMore ? 'Loading...' : 'Load More'}
                            </button>
                        </div>
                    {/if}
                </div>
            {/if}
        {/if}
    </div>
</Layout>